import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Flex,
  Text,
  Input,
  FormControl,
  FormLabel,
  Checkbox,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import CTable from "../../components/Table/table";
import {
  getMessagesByReceiverPhoneRequest,
  sendSavedMessagesRequest
} from "../../modules/admin-modules/reports/Actions";
import { getTeacherSubjectsRequest } from "../../modules/admin-modules/teachers/Actions";
import Layout from "../../components/dashboard/Layout";
import LogoutMenu from "../../components/dashboard/Layout/logout-menu";
import { toast } from "react-toastify";
import Select from "react-select";

// Optimized checkbox component to prevent unnecessary re-renders
const MessageCheckbox = React.memo(({ messageId, isSelected, onToggle }) => (
  <Checkbox
    key={`checkbox-${messageId}`}
    isChecked={isSelected}
    onChange={(e) => onToggle(messageId, e.target.checked)}
  />
));

const SavedMessagesByPhone = () => {
  const dispatch = useDispatch();

  // State management
  const [receiverPhone, setReceiverPhone] = useState("");
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);

  // Redux selectors
  const savedMessagesByPhone = useSelector((state) => state.reports.savedMessagesByReceiverPhone);
  const pagination = useSelector((state) => state.reports.savedMessagesByReceiverPhonePagination);
  const reportsLoading = useSelector((state) => state.reports.isLoading);
  const teacherSubjects = useSelector((state) => state.teachers.teacherSubjects);

  // Table columns
  const columns = [
    {
      Header: "Select",
      accessor: "select",
    },
    {
      Header: "Message",
      accessor: "message",
    },
    {
      Header: "Receiver Phone",
      accessor: "receiver_phone",
    },
    {
      Header: "Created At",
      accessor: "created_at",
    },
  ];

  // Optimized message selection handler
  const handleMessageToggle = useCallback((messageId, isChecked) => {
    setSelectedMessages(prev => {
      if (isChecked) {
        return [...prev, messageId];
      } else {
        return prev.filter(id => id !== messageId);
      }
    });
  }, []);

  // Optimized table data with useMemo
  const tableData = useMemo(() => {
    return (savedMessagesByPhone || [])?.map((message) => ({
      ...message,
      select: (
        <MessageCheckbox
          messageId={message.id}
          isSelected={selectedMessages.includes(message.id)}
          onToggle={handleMessageToggle}
        />
      ),
      created_at: new Date(message.created_at).toLocaleString(),
    })) || [];
  }, [savedMessagesByPhone, selectedMessages, handleMessageToggle]);

  // Handle fetch messages by phone
  const handleFetchMessages = useCallback((page = 1) => {
    if (!receiverPhone.trim()) {
      toast.error("Please enter a phone number");
      return;
    }

    const payload = {
      receiver_phone: receiverPhone.trim(),
      page: page,
    };

    dispatch(getMessagesByReceiverPhoneRequest(payload));
  }, [receiverPhone, dispatch]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setSelectedMessages([]); // Clear selected messages when changing pages
    handleFetchMessages(newPage);
  }, [handleFetchMessages]);

  // Handle send selected messages
  const handleSendMessages = useCallback(() => {
    if (selectedMessages.length === 0) {
      toast.error("Please select at least one message to send");
      return;
    }

    if (!selectedSubject) {
      toast.error("Please select a subject");
      return;
    }

    const payload = {
      message_ids: selectedMessages,
      user_id: selectedSubject,
    };

    dispatch(sendSavedMessagesRequest(payload));
    setSelectedMessages([]);
  }, [selectedMessages, dispatch, selectedSubject]);

  // Optimized handle select all messages
  const handleSelectAll = useCallback((isChecked) => {
    if (isChecked) {
      setSelectedMessages((savedMessagesByPhone || [])?.map(message => message.id) || []);
    } else {
      setSelectedMessages([]);
    }
  }, [savedMessagesByPhone]);

  // Handle phone number input validation
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and common phone formatting characters
    const phoneRegex = /^[0-9+\-\s()]*$/;
    if (phoneRegex.test(value) || value === "") {
      setReceiverPhone(value);
    }
  };

  // Teacher subjects options for select
  const teacherSubjectsOptions = teacherSubjects?.data?.map((subject) => ({
    value: subject.id,
    label: subject.subject.name,
  }));

  // Fetch teacher subjects on component mount
  useEffect(() => {
    dispatch(getTeacherSubjectsRequest());
  }, [dispatch]);

  // Select component styles
  const colourStyles = {
    control: (styles) => ({
      ...styles,
      width: "100%",
      background: "#f5f5f5",
      color: "#690000",
      borderRadius: "16px",
    }),
    option: (styles) => ({ ...styles, color: "#690000" }),
  };

  return (
    <Layout>
      <div style={{ minHeight: "400px" }}>
        <Flex
          w="100%"
          my="30px"
          justifyContent="space-between"
          alignItems="center"
          position="relative"
          zIndex="10000000"
        >
          <Text fontSize="24px" fontWeight="600" color="main_1">
            Messages by Phone Number
          </Text>
          <LogoutMenu />
        </Flex>

        {/* Phone Number Filter */}
        <Box mb={6} p={4} bg="gray.50" borderRadius="md">
          <Text fontSize="16px" fontWeight="500" mb={4}>
            Search Messages by Phone Number
          </Text>
          <Flex gap={4} alignItems="end">
            <FormControl flex="1">
              <FormLabel>Receiver Phone Number</FormLabel>
              <Input
                type="tel"
                value={receiverPhone}
                onChange={handlePhoneChange}
                placeholder="Enter phone number (e.g., +1234567890)"
                bg="white"
              />
            </FormControl>
              <Button
                colorScheme="blue"
                onClick={() => handleFetchMessages(1)}
                isLoading={reportsLoading}
                loadingText="Searching..."
                disabled={!receiverPhone.trim()}
              >
                Search Messages
              </Button>
          </Flex>
        </Box>

        {/* Subject Selection */}
        <Box mb={6} p={4} bg="gray.50" borderRadius="md" w="50%">
          <Text fontSize="16px" fontWeight="500" mb={4}>
            Select Subject for Message Sending
          </Text>
          <Select
            options={teacherSubjectsOptions}
            onChange={(data) => {
              setSelectedSubject(data.value);
            }}
            styles={colourStyles}
            placeholder="Select Subject - المادة"
            value={teacherSubjectsOptions?.find(option => option.value === selectedSubject)}
          />
        </Box>

        {/* Selected Messages Summary */}
        {selectedMessages.length > 0 && (
          <Box mb={4} p={4} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
            <Flex justifyContent="space-between" alignItems="center">
              <Text color="orange.800" fontWeight="500">
                {selectedMessages.length} message(s) selected
              </Text>
              <Button
                colorScheme="orange"
                onClick={handleSendMessages}
                isLoading={reportsLoading}
                loadingText="Sending..."
                disabled={selectedMessages.length === 0 || !selectedSubject}
              >
                Send Selected Messages
              </Button>
            </Flex>
          </Box>
        )}

        {/* Messages Table */}
        {savedMessagesByPhone && savedMessagesByPhone.length > 0 && (
          <Box>
            <Flex justifyContent="space-between" alignItems="center" mb={4}>
              <Text fontSize="18px" fontWeight="500">
                Messages for {receiverPhone} ({pagination.total} total, showing {pagination.from}-{pagination.to})
              </Text>
              <Checkbox
                isChecked={selectedMessages.length === (savedMessagesByPhone || [])?.length && (savedMessagesByPhone || [])?.length > 0}
                isIndeterminate={selectedMessages.length > 0 && selectedMessages.length < ((savedMessagesByPhone || [])?.length || 0)}
                onChange={(e) => handleSelectAll(e.target.checked)}
              >
                Select All
              </Checkbox>
            </Flex>

            <CTable
              Columns={columns}
              Data={tableData}
              Actions={false}
              currentpage={pagination.currentPage}
              setPageNumber={handlePageChange}
              isLoading={reportsLoading}
              totalPage={pagination.totalPages}
            />
          </Box>
        )}

        {/* No messages found */}
        {savedMessagesByPhone && savedMessagesByPhone.length === 0 && !reportsLoading && receiverPhone && (
          <Box p={6} textAlign="center" bg="gray.50" borderRadius="md">
            <Text fontSize="16px" color="gray.600">
              No messages found for phone number: {receiverPhone}
            </Text>
          </Box>
        )}

        {/* Instructions */}
        {!savedMessagesByPhone.length && !reportsLoading && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Instructions</AlertTitle>
              <AlertDescription>
                Enter a phone number to search for messages sent to that specific number. Select a subject for message sending.
                Results are paginated, and you can navigate through pages using the table controls. You can select individual messages
                or use "Select All" to choose multiple messages for resending.
              </AlertDescription>
            </Box>
          </Alert>
        )}
      </div>
    </Layout>
  );
};

export default SavedMessagesByPhone;
